import { PlusOutlined } from '@ant-design/icons';
import { Button, Space } from 'antd';
import { runInAction } from 'mobx';
import { observer, useLocalStore } from 'mobx-react-lite';
import * as React from 'react';
import MLModel from '../../../ml_models/models/MLModel';
import { MLService } from '../../../ml_models/services';
import PromptTemplateService from '../../../prompt_templates/services/PromptTemplatesService';
import { PromptTemplate } from '../../../prompt_templates/types';
import CustomBlock, { CustomBlockType, hasPipeline, PipelineStep } from './CustomBlock';
import './CustomBlockConfigurator.less';

interface Props {
    value?: string;
    onChange?: (value: string) => void;
    projectId: string;
    disabled?: boolean;
}

const CustomBlockConfigurator: React.FC<Props> = ({ value, onChange, projectId, disabled = false }) => {
    const blocks = React.useMemo(() => {
        if (!value) {
            return [];
        }
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return parsed as CustomBlockType[];
            }
            return [];
        } catch (error) {
            console.error('Failed to parse custom blocks JSON:', error);
            return [];
        }
    }, [value]);

    const store = useLocalStore(() => ({
        mlModels: [] as MLModel[],
        promptTemplates: [] as PromptTemplate[],
        isLoadingModels: false,
        isLoadingPrompts: false,
        mlService: new MLService(),
        promptService: new PromptTemplateService(),

        async loadMlModels() {
            try {
                runInAction(() => {
                    this.isLoadingModels = true;
                });

                const models = await this.mlService.getMLModelsForProject(projectId);

                runInAction(() => {
                    this.mlModels = models;
                });
            } catch (error) {
                console.error('Failed to load ML models:', error);
            } finally {
                runInAction(() => {
                    this.isLoadingModels = false;
                });
            }
        },

        async loadPromptTemplates() {
            try {
                runInAction(() => {
                    this.isLoadingPrompts = true;
                });

                const templates = await this.promptService.getPromptTemplates(projectId);

                runInAction(() => {
                    this.promptTemplates = templates;
                });
            } catch (error) {
                console.error('Failed to load prompt templates:', error);
            } finally {
                runInAction(() => {
                    this.isLoadingPrompts = false;
                });
            }
        }
    }));

    React.useEffect(() => {
        store.loadMlModels();
        store.loadPromptTemplates();
    }, [projectId, store]);

    const handleAddBlock = () => {
        const newBlock: CustomBlockType = {
            type: 'INTERPRETATION_BLOCK',
            pipeline: []
        };
        const updatedValue = [...blocks, newBlock];
        onChange?.(JSON.stringify(updatedValue));
    };

    const handleRemoveBlock = (index: number) => {
        const updatedValue = blocks.filter((_, i) => i !== index);
        onChange?.(JSON.stringify(updatedValue));
    };

    const handleBlockTypeChange = (index: number, newType: 'SMART_BLOCK' | 'INTERPRETATION_BLOCK') => {
        const updatedValue = [...blocks];
        if (newType === 'SMART_BLOCK') {
            updatedValue[index] = {
                type: 'SMART_BLOCK',
                model_id: ''
            };
        } else {
            updatedValue[index] = {
                type: 'INTERPRETATION_BLOCK',
                pipeline: []
            };
        }
        onChange?.(JSON.stringify(updatedValue));
    };

    const handleSmartBlockModelChange = (index: number, modelId: string) => {
        const updatedValue = [...blocks];
        const block = updatedValue[index];

        if (!block || block.type !== 'SMART_BLOCK') {
            return;
        }

        updatedValue[index] = {
            ...block,
            model_id: modelId
        };
        onChange?.(JSON.stringify(updatedValue));
    };

    const handleAddPipelineStep = (blockIndex: number) => {
        const updatedValue = [...blocks];
        const block = updatedValue[blockIndex];

        if (!hasPipeline(block)) {
            return;
        }

        const newStep: PipelineStep = {
            type: 'image',
            model_id: ''
        };

        updatedValue[blockIndex] = {
            ...block,
            pipeline: [...block.pipeline, newStep]
        } as CustomBlockType;

        onChange?.(JSON.stringify(updatedValue));
    };

    const handleRemovePipelineStep = (blockIndex: number, stepIndex: number) => {
        const updatedValue = [...blocks];
        const block = updatedValue[blockIndex];

        if (!hasPipeline(block)) {
            return;
        }

        updatedValue[blockIndex] = {
            ...block,
            pipeline: block.pipeline.filter((_, i) => i !== stepIndex)
        } as CustomBlockType;
        onChange?.(JSON.stringify(updatedValue));
    };

    const handlePipelineStepTypeChange = (
        blockIndex: number,
        stepIndex: number,
        newType: 'image' | 'text' | 'remote'
    ) => {
        const updatedValue = [...blocks];
        const block = updatedValue[blockIndex];

        if (!hasPipeline(block)) {
            return;
        }

        const typeMap: Record<string, PipelineStep> = {
            remote: { type: 'remote', url: '' },
            image: { type: 'image', model_id: '' },
            text: { type: 'text', model_id: '' }
        };

        const step = typeMap[newType];
        if (!step) {
            return;
        }

        updatedValue[blockIndex] = {
            ...block,
            pipeline: block.pipeline.map((s, i) => (i === stepIndex ? step : s))
        } as CustomBlockType;

        onChange?.(JSON.stringify(updatedValue));
    };

    const handlePipelineStepFieldChange = (
        blockIndex: number,
        stepIndex: number,
        field: string,
        fieldValue: string | boolean | undefined
    ) => {
        const updatedValue = [...blocks];
        const block = updatedValue[blockIndex];

        if (!hasPipeline(block)) {
            return;
        }

        const currentStep = block.pipeline[stepIndex];
        let step = { ...currentStep } as PipelineStep;

        if (fieldValue === undefined || fieldValue === '') {
            if (field in step) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                delete (step as any)[field];
            }
        } else {
            step = { ...currentStep, [field]: fieldValue } as PipelineStep;
        }

        updatedValue[blockIndex] = {
            ...block,
            pipeline: block.pipeline.map((s, i) => (i === stepIndex ? step : s))
        } as CustomBlockType;

        onChange?.(JSON.stringify(updatedValue));
    };

    const handleMovePipelineStep = (blockIndex: number, dragIndex: number, hoverIndex: number) => {
        const updatedValue = [...blocks];
        const block = updatedValue[blockIndex];

        if (!hasPipeline(block)) {
            return;
        }

        const newPipeline = [...block.pipeline];
        const [draggedStep] = newPipeline.splice(dragIndex, 1);
        newPipeline.splice(hoverIndex, 0, draggedStep);

        updatedValue[blockIndex] = {
            ...block,
            pipeline: newPipeline
        } as CustomBlockType;

        onChange?.(JSON.stringify(updatedValue));
    };

    return (
        <div className="custom-block-configurator">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {blocks.map((block, index) => (
                    <CustomBlock
                        key={index}
                        block={block}
                        index={index}
                        disabled={disabled}
                        store={store}
                        onRemoveBlock={handleRemoveBlock}
                        onBlockTypeChange={handleBlockTypeChange}
                        onSmartBlockModelChange={handleSmartBlockModelChange}
                        onAddPipelineStep={handleAddPipelineStep}
                        onRemovePipelineStep={handleRemovePipelineStep}
                        onPipelineStepTypeChange={handlePipelineStepTypeChange}
                        onPipelineStepFieldChange={handlePipelineStepFieldChange}
                        onMovePipelineStep={handleMovePipelineStep}
                    />
                ))}

                <Button onClick={handleAddBlock} icon={<PlusOutlined />} disabled={disabled} block>
                    Add Block
                </Button>
            </Space>
        </div>
    );
};

export default observer(CustomBlockConfigurator);
